import React, { memo } from 'react'
import type { SceneEdge, ScenePoint } from '../api/types'

/** Use pre-resolved color from Scene JSON, fall back to CSS var for edge particles. */
export function getEdgeColor(edge: SceneEdge): string {
    return edge.color
}

export function buildSmoothPath(points: { x: number; y: number }[], radius = 8): string {
    if (points.length < 2) return ''
    if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`
    let d = `M ${points[0].x} ${points[0].y}`
    for (let i = 1; i < points.length - 1; i++) {
        const prev = points[i - 1], curr = points[i], next = points[i + 1]
        const dx1 = curr.x - prev.x, dy1 = curr.y - prev.y
        const dx2 = next.x - curr.x, dy2 = next.y - curr.y
        const len1 = Math.hypot(dx1, dy1), len2 = Math.hypot(dx2, dy2)
        if (len1 === 0 || len2 === 0) { d += ` L ${curr.x} ${curr.y}`; continue }
        const r = Math.min(radius, len1 / 2, len2 / 2)
        const bx = curr.x - (dx1 / len1) * r, by = curr.y - (dy1 / len1) * r
        const ax = curr.x + (dx2 / len2) * r, ay = curr.y + (dy2 / len2) * r
        d += ` L ${bx} ${by} Q ${curr.x} ${curr.y} ${ax} ${ay}`
    }
    d += ` L ${points[points.length - 1].x} ${points[points.length - 1].y}`
    return d
}

// ── Edge label position (mirror SvgRenderer.kt) ─────────────────────

/**
 * Arc-length midpoint of a polyline. Walks the full path, finds the
 * point at half the total length, and interpolates within that segment.
 *
 * Picking the *index* midpoint instead lands on a corner vertex for
 * multi-segment orthogonal routes (which is what ELK's layered router
 * produces), pulling labels sideways away from the visual centre.
 *
 * Mirrors `SvgRenderer.edgeMidpoint` in core/render.
 */
export function edgeMidpoint(points: ScenePoint[]): { x: number; y: number } {
    if (points.length === 0) return { x: 0, y: 0 }
    if (points.length === 1) return { x: points[0].x, y: points[0].y }
    let total = 0
    for (let i = 1; i < points.length; i++) {
        total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y)
    }
    const target = total / 2
    let accum = 0
    for (let i = 1; i < points.length; i++) {
        const dx = points[i].x - points[i - 1].x
        const dy = points[i].y - points[i - 1].y
        const segLen = Math.hypot(dx, dy)
        if (accum + segLen >= target) {
            const t = segLen > 0 ? (target - accum) / segLen : 0
            return { x: points[i - 1].x + dx * t, y: points[i - 1].y + dy * t }
        }
        accum += segLen
    }
    const last = points[points.length - 1]
    return { x: last.x, y: last.y }
}

/**
 * Resolved label position for an edge. Uses ELK's `labelPosition` verbatim
 * when populated; falls back to the arc-length midpoint only when ELK
 * didn't produce one (mrtree / radial algorithms don't populate edge
 * label positions).
 *
 * Mirrors `SvgRenderer.resolveLabelPosition`.
 */
export function resolveLabelPosition(edge: SceneEdge): { x: number; y: number } {
    const elkPos = edge.labelPosition
    if (!elkPos) return edgeMidpoint(edge.points)
    return { x: elkPos.x, y: elkPos.y }
}

// ── Edge shape (path only, no arrowhead) ────────────────────────────

export const EdgeShape = memo(function EdgeShape({
    edge, lightweight = false,
}: { edge: SceneEdge; lightweight?: boolean }) {
    if (edge.points.length < 2) return null
    const pathD = buildSmoothPath(edge.points, 12)
    const color = edge.color
    const dash = edge.dashPattern ? edge.dashPattern.join(',') : undefined

    if (lightweight) {
        return (
            <path
                d={pathD} fill="none" stroke={color} strokeWidth={2}
                opacity={0.5} strokeLinecap="round" strokeLinejoin="round"
            />
        )
    }

    return (
        <path
            d={pathD} fill="none" stroke={color}
            strokeWidth={edge.strokeWidth}
            opacity={edge.opacity}
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={dash}
            style={{ transition: 'stroke-width 0.2s ease, opacity 0.2s ease' }}
        />
    )
})

// ── Arrow head (rendered in a separate layer ABOVE nodes) ───────────

export const EdgeArrowHead = memo(function EdgeArrowHead({
    edge,
}: { edge: SceneEdge }) {
    if (!edge.arrowHead) return null
    const a = edge.arrowHead
    const color = edge.color
    return (
        <path
            d={`M ${a.tip.x} ${a.tip.y} L ${a.left.x} ${a.left.y} L ${a.right.x} ${a.right.y} Z`}
            fill={color} opacity={edge.opacity}
        />
    )
})

/**
 * Collect ELK's label position for every labelled edge in one pass.
 * Returns a Map<edge.id, {x, y}>; edges with no label are absent.
 */
export function resolveAllLabelPositions(edges: SceneEdge[]): Map<string, { x: number; y: number }> {
    const out = new Map<string, { x: number; y: number }>()
    for (const edge of edges) {
        const labelText = edge.label ?? ''
        if (!labelText || edge.labelMode === 'none') continue
        if (edge.points.length < 2) continue
        out.set(edge.id, resolveLabelPosition(edge))
    }
    return out
}

// ── Edge label component ────────────────────────────────────────────

/** Edge label rendered as an opaque chip + text at ELK's chosen position. */
export const EdgeLabel = memo(function EdgeLabel({
    edge, resolvedPosition,
}: { edge: SceneEdge; resolvedPosition?: { x: number; y: number } }) {
    if (edge.points.length < 2) return null
    const labelText = edge.label ?? ''
    if (!labelText || edge.labelMode === 'none') return null

    const pos = resolvedPosition ?? resolveLabelPosition(edge)
    const color = edge.color
    const labelFontSize = 11
    const charW = labelFontSize * 0.62
    const labelW = labelText.length * charW + 12
    const labelH = labelFontSize + 8

    return (
        <g style={{ pointerEvents: 'none' }}>
            <rect
                x={pos.x - labelW / 2} y={pos.y - labelH / 2}
                width={labelW} height={labelH}
                fill="var(--bg)" fillOpacity={0.96}
                stroke={color} strokeWidth={0.6} strokeOpacity={0.4}
                rx={4} ry={4}
            />
            <text
                x={pos.x} y={pos.y}
                fontSize={labelFontSize} fontWeight={500} fontFamily="var(--font-mono)"
                fill={color} opacity={0.9}
                textAnchor="middle" dominantBaseline="central"
            >
                {labelText}
            </text>
        </g>
    )
})

/**
 * Edge marker glyph rendered at the polyline midpoint — used by
 * back-edge `↻` markers (Phase 1) and similar single-character marks.
 * Independent of the edge label.
 */
export const EdgeMarker = memo(function EdgeMarker({ edge }: { edge: SceneEdge }) {
    if (!edge.marker || edge.points.length < 2) return null
    const mid = edgeMidpoint(edge.points)
    return (
        <text
            x={mid.x} y={mid.y}
            fontSize={14} fontWeight={700}
            fill={edge.color} opacity={0.9}
            textAnchor="middle" dominantBaseline="central"
            style={{ pointerEvents: 'none' }}
        >
            {edge.marker}
        </text>
    )
})

export const EdgeParticles = memo(function EdgeParticles({
    edge, speed,
}: { edge: SceneEdge; speed: number }) {
    if (edge.points.length < 2) return null
    const pts = edge.points
    const startX = pts[0].x, startY = pts[0].y
    let relPath = 'M 0 0'
    for (let i = 1; i < pts.length; i++) relPath += ` L ${pts[i].x - startX} ${pts[i].y - startY}`
    const color = edge.color
    const duration = Math.max(0.8, 3.5 / speed)

    return (
        <g transform={`translate(${startX}, ${startY})`}>
            {[0, 1, 2].map(i => {
                const delay = (i * duration) / 3
                return (
                    <React.Fragment key={i}>
                        <circle r={3.5} fill={color} opacity={0}>
                            <animateMotion dur={`${duration}s`} begin={`${delay}s`} repeatCount="indefinite" path={relPath} />
                            <animate attributeName="opacity" values="0;0.95;0.95;0" keyTimes="0;0.05;0.85;1" dur={`${duration}s`} begin={`${delay}s`} repeatCount="indefinite" />
                        </circle>
                        <circle r={8} fill={color} filter="url(#particle-glow)" opacity={0}>
                            <animateMotion dur={`${duration}s`} begin={`${delay}s`} repeatCount="indefinite" path={relPath} />
                            <animate attributeName="opacity" values="0;0.25;0.25;0" keyTimes="0;0.05;0.85;1" dur={`${duration}s`} begin={`${delay}s`} repeatCount="indefinite" />
                        </circle>
                    </React.Fragment>
                )
            })}
        </g>
    )
})
